import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { MessageCircle, Mountain } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function Messages() {
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/messages/threads");
                setThreads(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div>
            <TopBar title="Messages" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:flex items-center gap-3 mb-8">
                    <MessageCircle className="w-8 h-8 text-ember-orange" />
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">
                        Messages
                    </h1>
                </div>

                {loading ? (
                    <div className="space-y-2" data-testid="messages-loading">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-20 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />
                        ))}
                    </div>
                ) : threads.length === 0 ? (
                    <div
                        data-testid="messages-empty"
                        className="bg-snowcap border border-ridge-line rounded-lg p-10 text-center"
                    >
                        <Mountain className="w-10 h-10 text-slate-gray mx-auto mb-4" />
                        <p className="font-display font-bold text-xl text-midnight-pine mb-2">
                            No conversations yet
                        </p>
                        <p className="text-slate-gray text-sm max-w-md mx-auto">
                            Open someone's profile and tap <strong>Message</strong> to start a thread. Ask about a route, swap beta, or share a ride.
                        </p>
                    </div>
                ) : (
                    <ul
                        data-testid="messages-thread-list"
                        className="bg-snowcap border border-ridge-line rounded-lg divide-y divide-ridge-line overflow-hidden"
                    >
                        {threads.map((t) => (
                            <li key={t.id}>
                                <Link
                                    to={`/messages/${t.id}`}
                                    data-testid={`messages-thread-${t.id}`}
                                    className="flex items-center gap-3 px-4 py-4 hover:bg-alpine-mist transition-colors"
                                >
                                    <div className="w-12 h-12 rounded-full overflow-hidden bg-trail-tan shrink-0 flex items-center justify-center">
                                        {t.other_user.avatar ? (
                                            <img
                                                src={t.other_user.avatar}
                                                alt={t.other_user.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <Mountain className="w-5 h-5 text-midnight-pine" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-bold text-midnight-pine truncate">
                                                {t.other_user.name}
                                            </span>
                                            {t.last_message_at && (
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray shrink-0">
                                                    {formatDistanceToNow(new Date(t.last_message_at), { addSuffix: true })}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between gap-3 mt-0.5">
                                            <p className={`text-sm truncate ${t.unread_count > 0 ? "text-granite font-semibold" : "text-slate-gray"}`}>
                                                {t.last_message || "Start the conversation…"}
                                            </p>
                                            {t.unread_count > 0 && (
                                                <span
                                                    data-testid={`messages-thread-unread-${t.id}`}
                                                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-ember-orange text-white text-[10px] font-bold leading-5 text-center shrink-0"
                                                >
                                                    {t.unread_count > 9 ? "9+" : t.unread_count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
